import { useEffect, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

import { useIsLdIdentifiedWithFlagsForDispensary } from 'src/utils/launch-darkly/identified-dispensary';

export const LD_DISPENSARY_IDENTIFY_TIMEOUT_MS = 3000;

/**
 * True once an LD identify for this dispensary has settled and the live context still has
 * it, so flags targeted by dispensary are authoritative. The context alone is not enough:
 * `_app` seeds it with the dispensary at boot, so it matches before the first identify,
 * while the flags on hand are still the bootstrap ones. It also turns true after a bounded
 * timeout, so callers fail closed instead of waiting forever. The timeout counts from when
 * the dispensary id is known, and restarts if it changes.
 */
export function useIsLdIdentifiedForDispensary(dispensaryId: string | null | undefined): boolean {
  // The dispensary id whose wait has expired. Cleared on any id change, so every visit to a
  // store, including a return, starts a fresh window.
  const [timedOutFor, setTimedOutFor] = useState<string | null>(null);
  const [lastDispensaryId, setLastDispensaryId] = useState(dispensaryId);
  if (lastDispensaryId !== dispensaryId) {
    setLastDispensaryId(dispensaryId);
    setTimedOutFor(null);
  }
  const isIdentified = useIsLdIdentifiedWithFlagsForDispensary(dispensaryId);

  useEffect(() => {
    if (isIdentified || !dispensaryId) {
      return undefined;
    }
    const timerId = setTimeout(() => setTimedOutFor(dispensaryId), LD_DISPENSARY_IDENTIFY_TIMEOUT_MS);
    return () => clearTimeout(timerId);
  }, [isIdentified, dispensaryId]);

  return isIdentified || (Boolean(dispensaryId) && timedOutFor === dispensaryId);
}

/** True only while LD's current context is this dispensary (no timeout). */
export function useIsLdContextForDispensary(dispensaryId: string | null | undefined): boolean {
  const client = useLDClient();
  // Re-render when flags change: identify() replaces the flag set.
  useFlags();
  // LDClient types getContext() as non-null, but it returns null before identify() runs.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const contextDispensaryId = client?.getContext()?.['ecomm-dispensary']?.key;
  return Boolean(dispensaryId) && contextDispensaryId === dispensaryId;
}
